import axiosInstance from '@/setup/axios/axiosInstance';

import { InventoryReturnRow } from '../types';

interface ApiReturnItem {
  id: string;
  return_no?: string;
  code?: string;
  name?: string;
  order_no?: string;
  order_id?: string;
  company_id?: string;
  company_name?: string;
  store_id?: string;
  store_name?: string;
  status?: string;
  status_code?: string;
  status_display?: string;
  qty?: string | number;
  quantity?: string | number;
  note?: string;
}

interface ApiReturnListResponse {
  results?: ApiReturnItem[];
  count?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const mapApiReturn = (row: ApiReturnItem): InventoryReturnRow => ({
  id: row.id,
  code: row.return_no || row.code || '',
  name: row.name || row.return_no || row.code || '',
  order_no: row.order_no || row.order_id || '',
  store_name: row.store_name || row.store_id || '',
  status: row.status_code || row.status || '',
  qty: Number(row.qty ?? row.quantity ?? 0),
  company_id: row.company_id || '',
  company_name: row.company_name || '',
  note: row.note || '',
});

export interface GetReturnsParams {
  companyId?: string;
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

export interface GetReturnsResponse {
  results: InventoryReturnRow[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export async function getReturns(params: GetReturnsParams = {}): Promise<GetReturnsResponse> {
  const query = new URLSearchParams();
  if (params.companyId && UUID_PATTERN.test(params.companyId)) {
    query.append('company', params.companyId);
  }
  if (params.search?.trim()) {
    query.append('search', params.search.trim());
  }
  if (params.status && params.status !== 'all') {
    query.append('status', params.status);
  }
  if (params.page) {
    query.append('page', String(params.page));
  }
  if (params.pageSize && params.pageSize > 0) {
    query.append('page_size', String(params.pageSize));
  }

  const qs = query.toString();
  const response = await axiosInstance.get<ApiReturnListResponse | ApiReturnItem[]>(
    `/inventory/returns/${qs ? `?${qs}` : ''}`
  );
  const data = response.data;
  const payload = Array.isArray(data) ? data : (data.results || []);

  if (Array.isArray(data)) {
    return {
      results: payload.map(mapApiReturn),
      count: payload.length,
      page: 1,
      pageSize: payload.length,
      totalPages: 1,
    };
  }

  return {
    results: payload.map(mapApiReturn),
    count: data.count || payload.length,
    page: data.page || params.page || 1,
    pageSize: data.page_size || params.pageSize || payload.length,
    totalPages: data.total_pages || 1,
  };
}
